import { FiCheck } from "react-icons/fi";
import { cn } from "utils/helpers";
import type { RoleTemplate } from "newui/features/RolesPermissions/types/RolesPermissions.type";

interface TemplateQuickAssignProps {
	templates: RoleTemplate[];
	selectedId: number | null;
	onSelect: (id: number) => void;
}

// Distinct role names across all sides of a template's maps_to (procurer/bidder/hybrid).
const templateRoleNames = (template: RoleTemplate): string[] => {
	const all = [
		...template.maps_to.procurer,
		...template.maps_to.bidder,
		...template.maps_to.hybrid,
	];
	return Array.from(new Set(all.map((role) => role.name)));
};

const isHybridTemplate = (template: RoleTemplate): boolean =>
	template.maps_to.hybrid.length > 0 ||
	(template.maps_to.procurer.length > 0 && template.maps_to.bidder.length > 0);

// The quick-assign template cards (ground truth: quick-assign-modal.html). Reused inside Step 1.
export const TemplateQuickAssign = ({
	templates,
	selectedId,
	onSelect,
}: TemplateQuickAssignProps) => {
	if (templates.length === 0) {
		return (
			<p className="text-sm text-gray-500" data-testid="templates-empty">
				No templates available.
			</p>
		);
	}

	return (
		<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2" data-testid="template-grid">
			{templates.map((template) => {
				const picked = selectedId === template.id;
				const roleNames = templateRoleNames(template);
				return (
					<button
						type="button"
						key={template.id}
						onClick={() => onSelect(template.id)}
						aria-pressed={picked}
						data-testid={`template-card-${template.id}`}
						className={cn(
							"relative rounded-lg border-2 p-4 text-left transition-colors",
							picked
								? "border-blue-500 bg-blue-50"
								: "border-gray-200 hover:border-blue-500 hover:shadow"
						)}
					>
						{picked && (
							<span
								className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-white"
								data-testid={`template-picked-${template.id}`}
							>
								<FiCheck size={12} />
							</span>
						)}
						<h4 className="mb-1 flex items-center gap-2 text-sm font-semibold">
							{template.name}
							{isHybridTemplate(template) && (
								<span className="rounded bg-blue-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
									Hybrid company
								</span>
							)}
						</h4>
						<p className="mb-2.5 text-xs text-gray-600">{template.description}</p>
						<div className="flex flex-wrap gap-1.5">
							{roleNames.map((roleName) => (
								<span
									key={roleName}
									className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
								>
									{roleName}
								</span>
							))}
						</div>
					</button>
				);
			})}
		</div>
	);
};
